import { useState } from "react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import {
    Container,
    Card,
    Stack,
    Typography,
    Divider,
    Alert,
    TextField,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
} from "@mui/material";
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import {
    inspectGymBuddy,
    previewAttachBuddy,
    executeAttachBuddy,
    previewTeardown,
    executeTeardown,
    previewDeactivate,
    executeDeactivate,
    previewReactivate,
    executeReactivate,
    previewRemoveMember,
    executeRemoveMember,
    InspectResult,
    GroupView,
    PreviewBase,
} from "../controllers/subscriptionGroup";

// ----------------------------------------------------------------------

const CATEGORY_COLOR: Record<
    string,
    "error" | "warning" | "info" | "success" | "default"
> = {
    C1_zombie_active_group: "warning",
    C2_orphaned_buddy_entitlement: "error",
    C3_group_killed_owner_paying: "warning",
    C4_seat_drift: "info",
    C5_unclaimed_seat: "info",
    C6_redundant_household_sub: "info",
    healthy: "success",
    offboarded: "default",
    no_group: "default",
};

const fmt = (unix: number | null | undefined) =>
    unix ? dayjs.unix(unix).format("YYYY-MM-DD") : "—";

type PendingAction = {
    kind: "attach" | "teardown" | "deactivate" | "reactivate" | "remove";
    groupId: string;
    label: string;
    memberUserId?: string;
    buddyEmail?: string;
    preview: PreviewBase & Record<string, any>;
    execute: () => Promise<any>;
    successMsg: (result: any) => string;
};

export default function GymBuddyOps() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();

    const [query, setQuery] = useState("");
    const [result, setResult] = useState<InspectResult | null>(null);
    const [inspecting, setInspecting] = useState(false);
    const [pending, setPending] = useState<PendingAction | null>(null);
    const [executing, setExecuting] = useState(false);
    const [attachEmail, setAttachEmail] = useState<Record<string, string>>({});

    const onInspect = async (q?: string) => {
        const query_ = (q ?? query).trim();
        if (!query_) return;
        setInspecting(true);
        try {
            const r = await inspectGymBuddy(query_);
            setResult(r);
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "Inspect failed",
                { variant: "error" }
            );
        } finally {
            setInspecting(false);
        }
    };

    // Load a preview, then open the confirm dialog.
    const startAction = async (
        loader: () => Promise<PreviewBase & Record<string, any>>,
        action: Omit<PendingAction, "preview">
    ) => {
        try {
            const preview = await loader();
            setPending({ ...action, preview });
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "Preview failed",
                { variant: "error" }
            );
        }
    };

    const confirmAction = async () => {
        if (!pending) return;
        setExecuting(true);
        try {
            const res = await pending.execute();
            enqueueSnackbar(pending.successMsg(res), { variant: "success" });
            setPending(null);
            await onInspect(result?.query);
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "Action failed",
                { variant: "error" }
            );
        } finally {
            setExecuting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title> Gym Buddy Ops | Admin Portal</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : "xl"}>
                <Typography variant="h3" component="h1" paragraph>
                    Gym Buddy Operations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Inspect a user's Gym Buddy state (search by email or userId) and
                    remediate stranded groups. Every action previews first; refunds
                    are done in the Stripe dashboard.
                </Typography>

                <Card sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Email or userId (sub)"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && onInspect()}
                        />
                        <LoadingButton
                            variant="contained"
                            size="large"
                            loading={inspecting}
                            onClick={() => onInspect()}
                        >
                            Inspect
                        </LoadingButton>
                    </Stack>
                </Card>

                {result && (
                    <>
                        <Card sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {result.email || result.resolvedUserId}
                            </Typography>
                            <Stack spacing={1} divider={<Divider flexItem />}>
                                <Row label="User ID" value={result.resolvedUserId} />
                                <Row label="Cognito" value={result.cognitoStatus || "—"} />
                                <Row label="Role" value={result.role} />
                                <Row
                                    label="Own subscriptions"
                                    value={
                                        result.ownSubscriptions.length
                                            ? result.ownSubscriptions
                                                  .map(
                                                      (s) =>
                                                          `${s.customerId} [${s.status}] → ${fmt(
                                                              s.accessUntil
                                                          )}`
                                                  )
                                                  .join(", ")
                                            : "none"
                                    }
                                />
                            </Stack>
                        </Card>

                        {result.groups.length === 0 && (
                            <Alert severity="info">
                                No Gym Buddy group found for this user.
                            </Alert>
                        )}

                        {result.groups.map((g) => (
                            <GroupCard
                                key={g.groupId}
                                g={g}
                                attachEmail={attachEmail[g.groupId] || ""}
                                setAttachEmail={(v) =>
                                    setAttachEmail((p) => ({ ...p, [g.groupId]: v }))
                                }
                                onDeactivate={() =>
                                    startAction(() => previewDeactivate(g.groupId), {
                                        kind: "deactivate",
                                        groupId: g.groupId,
                                        label: "Deactivate group",
                                        execute: () => executeDeactivate(g.groupId),
                                        successMsg: () =>
                                            `Group ${g.groupId} deactivated`,
                                    })
                                }
                                onReactivate={() =>
                                    startAction(() => previewReactivate(g.groupId), {
                                        kind: "reactivate",
                                        groupId: g.groupId,
                                        label: "Reactivate group",
                                        execute: () => executeReactivate(g.groupId),
                                        successMsg: () =>
                                            `Group ${g.groupId} reactivated`,
                                    })
                                }
                                onTeardown={() =>
                                    startAction(() => previewTeardown(g.groupId), {
                                        kind: "teardown",
                                        groupId: g.groupId,
                                        label: "Tear down group (revoke buddies)",
                                        execute: () => executeTeardown(g.groupId),
                                        successMsg: (r) =>
                                            `Torn down (${r.method}); ${r.buddiesOffboarded} buddy(ies) offboarded`,
                                    })
                                }
                                onAttach={() => {
                                    const email = (attachEmail[g.groupId] || "").trim();
                                    if (!email) {
                                        enqueueSnackbar("Enter a buddy email/userId", {
                                            variant: "warning",
                                        });
                                        return;
                                    }
                                    const body = email.includes("@")
                                        ? { groupId: g.groupId, buddyEmail: email }
                                        : { groupId: g.groupId, buddyUserId: email };
                                    startAction(() => previewAttachBuddy(body), {
                                        kind: "attach",
                                        groupId: g.groupId,
                                        label: `Attach buddy ${email}`,
                                        buddyEmail: email,
                                        execute: () => executeAttachBuddy(body),
                                        successMsg: (r) =>
                                            `Attached buddy ${r.buddyUserId}`,
                                    });
                                }}
                                onRemove={(memberUserId, memberEmail) =>
                                    startAction(
                                        () =>
                                            previewRemoveMember({
                                                groupId: g.groupId,
                                                memberUserId,
                                            }),
                                        {
                                            kind: "remove",
                                            groupId: g.groupId,
                                            memberUserId,
                                            label: `Remove buddy ${memberEmail}`,
                                            execute: () =>
                                                executeRemoveMember({
                                                    groupId: g.groupId,
                                                    memberUserId,
                                                }),
                                            successMsg: () =>
                                                `Removed buddy ${memberEmail}`,
                                        }
                                    )
                                }
                            />
                        ))}
                    </>
                )}
            </Container>

            {/* Confirm dialog */}
            <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm: {pending?.label}</DialogTitle>
                <DialogContent>
                    {pending?.preview.blockers?.length ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <b>Blocked:</b>
                            <ul style={{ margin: "4px 0 0" }}>
                                {pending.preview.blockers.map((b) => (
                                    <li key={b}>{b}</li>
                                ))}
                            </ul>
                        </Alert>
                    ) : null}
                    {pending?.preview.alerts?.length ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <ul style={{ margin: "4px 0 0" }}>
                                {pending.preview.alerts.map((a) => (
                                    <li key={a}>{a}</li>
                                ))}
                            </ul>
                        </Alert>
                    ) : null}
                    <Box
                        component="pre"
                        sx={{
                            fontSize: 12,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                            m: 0,
                        }}
                    >
                        {JSON.stringify(
                            pending
                                ? Object.fromEntries(
                                      Object.entries(pending.preview).filter(
                                          ([k]) =>
                                              !["blockers", "alerts", "canExecute"].includes(
                                                  k
                                              )
                                      )
                                  )
                                : {},
                            null,
                            2
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)}>Cancel</Button>
                    <LoadingButton
                        variant="contained"
                        color="error"
                        loading={executing}
                        disabled={!pending?.preview.canExecute}
                        onClick={confirmAction}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ----------------------------------------------------------------------

function GroupCard({
    g,
    attachEmail,
    setAttachEmail,
    onDeactivate,
    onReactivate,
    onTeardown,
    onAttach,
    onRemove,
}: {
    g: GroupView;
    attachEmail: string;
    setAttachEmail: (v: string) => void;
    onDeactivate: () => void;
    onReactivate: () => void;
    onTeardown: () => void;
    onAttach: () => void;
    onRemove: (memberUserId: string, memberEmail: string) => void;
}) {
    const showDeactivate = g.category === "C1_zombie_active_group";
    const showTeardown =
        g.category === "C2_orphaned_buddy_entitlement" ||
        g.members.some((m) => m.status === "active");
    const showReactivate = g.category === "C3_group_killed_owner_paying";
    const showAttach =
        g.category === "C5_unclaimed_seat" ||
        g.category === "C6_redundant_household_sub" ||
        (g.status === "active" && g.currentSeats < g.maxSeats);

    return (
        <Card sx={{ p: 3, mb: 3 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
                flexWrap="wrap"
            >
                <Chip
                    label={g.category}
                    color={CATEGORY_COLOR[g.category] || "default"}
                />
                <Typography variant="subtitle1">{g.groupId}</Typography>
            </Stack>

            <Alert severity={CATEGORY_COLOR[g.category] === "success" ? "success" : "info"} sx={{ mb: 2 }}>
                {g.diagnosis}
                {g.recommended && g.recommended !== "none" && (
                    <>
                        <br />
                        <b>Recommended:</b> {g.recommended}
                    </>
                )}
            </Alert>

            {g.groupId !== "(none)" && (
                <Stack spacing={1} divider={<Divider flexItem />} sx={{ mb: 2 }}>
                    <Row label="Status" value={g.status} />
                    <Row label="Seats" value={`${g.currentSeats}/${g.maxSeats}`} />
                    <Row label="Product" value={g.productId} />
                    <Row
                        label="Owner Stripe (live / snapshot)"
                        value={`${g.ownerLiveStripeStatus ?? "gone"} / ${
                            g.ownerSnapshotStatus ?? "—"
                        } → ${fmt(g.ownerAccessUntil)}`}
                    />
                    {g.members.map((m) => (
                        <Stack
                            key={m.userId}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                        >
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                {m.email} [{m.status}] — entitlement:{" "}
                                {m.entitlementStatus ?? "none"} → {fmt(m.accessUntil)}
                            </Typography>
                            {m.status === "active" && (
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => onRemove(m.userId, m.email)}
                                >
                                    Remove
                                </Button>
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}

            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                {showDeactivate && (
                    <Button variant="contained" color="warning" onClick={onDeactivate}>
                        Deactivate group
                    </Button>
                )}
                {showReactivate && (
                    <Button variant="contained" color="success" onClick={onReactivate}>
                        Reactivate group
                    </Button>
                )}
                {showTeardown && (
                    <Button variant="contained" color="error" onClick={onTeardown}>
                        Tear down (revoke buddies)
                    </Button>
                )}
                {showAttach && g.groupId !== "(none)" && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            label="Buddy email/userId"
                            value={attachEmail}
                            onChange={(e) => setAttachEmail(e.target.value)}
                        />
                        <Button variant="contained" onClick={onAttach}>
                            Attach buddy
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Card>
    );
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.5 }}
        >
            <Typography variant="subtitle2" color="text.secondary">
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{ textAlign: "right", wordBreak: "break-all" }}
            >
                {value}
            </Typography>
        </Stack>
    );
}
